// Note: This service is designed to be used with useSession hook
// The sessionToken should be passed from the calling component

import { Team, TeamMember } from '../components/teams/types';

export interface CreateTeamDto {
  name: string;
  description?: string;
  maxSeats: number;
  allowMemberInvites: boolean;
  allowMemberPurchases: boolean;
  allowMemberFollows: boolean;
}

export interface InviteMemberDto {
  email: string;
  role: 'admin' | 'member';
}

export interface UpdateTeamDto {
  name?: string;
  description?: string;
  maxSeats?: number;
  allowMemberInvites?: boolean;
  allowMemberPurchases?: boolean;
  allowMemberFollows?: boolean;
  status?: 'active' | 'suspended' | 'cancelled';
}

export interface UpdateTeamMemberDto {
  role?: 'admin' | 'member';
  isActive?: boolean;
}

export interface UserSubscription {
  planType: 'free' | 'starter' | 'pro' | 'advanced';
}

// Client-side API request function
const makeClientRequest = async (url: string, sessionToken: string, options: RequestInit = {}): Promise<any> => {
  console.log('[makeClientRequest] Starting request:', {
    url,
    method: options.method || 'GET',
  });

  try {
    const res = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionToken}`,
      },
    });

    console.log('[makeClientRequest] Fetch response received:', {
      status: res.status,
      statusText: res.statusText,
    });

    if (!res.ok) {
      let errorMessage = res.statusText || 'Request failed';
      try {
        const errorData = await res.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        // If we can't parse the error response, use the status text
      }
      throw new Error(errorMessage);
    }

    return await res.json();
  } catch (error: any) {
    console.error('[makeClientRequest] Request error:', {
      message: error.message,
      cause: error.cause,
    });

    // Handle 401 errors by redirecting to login
    if (error.message?.includes('401') || error.cause === 401) {
      console.log('[makeClientRequest] 401 Unauthorized - redirecting to login');
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname + window.location.search;
        window.location.href = `/login?redirect_to=${encodeURIComponent(currentPath)}`;
      }
    }

    throw error;
  }
};

// Client-side Teams API Functions
export async function updateTeam(teamId: string, teamData: UpdateTeamDto, sessionToken: string): Promise<Team> {
  const url = `/api/v1/teams/${teamId}`;
  return makeClientRequest(url, sessionToken, {
    method: 'PATCH',
    body: JSON.stringify(teamData),
  });
}

export async function deleteTeam(teamId: string, sessionToken: string): Promise<void> {
  const url = `/api/v1/teams/${teamId}`;
  return makeClientRequest(url, sessionToken, {
    method: 'DELETE',
  });
}

export async function inviteMember(teamId: string, memberData: InviteMemberDto, sessionToken: string): Promise<TeamMember> {
  const url = `/api/v1/teams/${teamId}/members`;
  return makeClientRequest(url, sessionToken, {
    method: 'POST',
    body: JSON.stringify(memberData),
  });
}

export async function updateTeamMember(teamId: string, memberId: string, memberData: UpdateTeamMemberDto, sessionToken: string): Promise<TeamMember> {
  const url = `/api/v1/teams/${teamId}/members/${memberId}`;
  return makeClientRequest(url, sessionToken, {
    method: 'PATCH',
    body: JSON.stringify(memberData),
  });
}

export async function removeTeamMember(teamId: string, memberId: string, sessionToken: string): Promise<void> {
  const url = `/api/v1/teams/${teamId}/members/${memberId}`;
  return makeClientRequest(url, sessionToken, {
    method: 'DELETE',
  });
}

export async function getTeamCredits(sessionToken: string): Promise<{
  hasTeam: boolean;
  teamId?: string;
  teamName?: string;
  totalCredits?: number;
  usedCredits?: number;
  availableCredits?: number;
  allowMemberPurchases?: boolean;
}> {
  const url = `/api/v1/user/companies/team-credits`;
  return makeClientRequest(url, sessionToken);
}

export interface TeamCreditsUsageItem {
  id: number;
  kind?: 'usage' | 'addition';
  amount: number;
  description: string;
  createdAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    photoUrl?: string;
  } | null;
  company: {
    id: number;
    name: string;
    domain: string;
    logoUrl: string | null;
    isContact?: boolean;
    contactName?: string;
    contactLinkedin?: string;
    companyId?: number;
    companyLogo?: string | null;
    isApiUsage?: boolean;
    apiCapability?: string;
    isPersonaUsage?: boolean;
    source?: 'app' | 'api' | 'mcp';
  } | null;
}

export interface TeamCreditsAddition {
  id: number;
  kind?: 'addition';
  amount: number;
  description: string;
  createdAt: string;
  type: 'subscription' | 'trial' | 'manual' | 'early_upgrade' | 'free_monthly' | 'payg' | 'stakeholder_refund';
  additionType?: 'subscription' | 'trial' | 'manual' | 'early_upgrade' | 'free_monthly' | 'payg' | 'stakeholder_refund';
  user?: TeamCreditsUsageItem['user'];
  company?: null;
}

export interface TeamCreditsUsageResponse {
  usage: TeamCreditsUsageItem[];
  additions: TeamCreditsAddition[];
  ledger?: Array<TeamCreditsUsageItem | TeamCreditsAddition>;
  totalUsed: number;
  totalAdded: number;
  companiesUnlocked: number;
  contactsUnlocked: number;
}

export async function getTeamCreditsUsage(teamId: string, sessionToken: string): Promise<TeamCreditsUsageResponse> {
  const url = `/api/v1/teams/${teamId}/credits/usage`;
  return makeClientRequest(url, sessionToken);
}

export async function acceptTeamInvitation(teamId: string, sessionToken: string): Promise<any> {
  const url = `/api/v1/teams/${teamId}/invitations/accept`;
  return makeClientRequest(url, sessionToken, {
    method: 'POST',
  });
}

export async function declineTeamInvitation(teamId: string, sessionToken: string): Promise<any> {
  const url = `/api/v1/teams/${teamId}/invitations/decline`;
  return makeClientRequest(url, sessionToken, {
    method: 'POST',
  });
}

// Re-export types for convenience
export type { Team, TeamMember, TeamCredits, PendingInvitation } from '../components/teams/types';
